import { z } from '@repo/schema'

import {
  addPhoneWithCountryValidation,
  createNullablePhoneNumberFieldSchema,
  createPhoneNumberFieldSchema,
  OptionalPhoneCountryCodeSchema,
  PhoneCountryCodeSchema,
} from './phone'
import { normalizeVatNumber, VAT_NUMBER_MESSAGE, VAT_NUMBER_PATTERN } from './vat'

const WEBSITE_PATTERN = /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/\S*)?$/i

export const CUSTOMER_SETUP_FORM_TOKEN_PATTERN = /^[A-Za-z0-9]{64}$/

export const CustomerSetupFormTokenValueSchema = z.string().regex(CUSTOMER_SETUP_FORM_TOKEN_PATTERN)

export const CreateCustomerSetupFormTokenInputSchema = z.object({
  company_id: z.number().int().positive().optional(),
})

export const GetCustomerSetupFormTokenCompanySchema = z.object({
  deleted: z.boolean(),
  id: z.number(),
  title: z.string(),
  vat: z.string().nullish(),
})

export const GetCustomerSetupFormTokenCreatorSchema = z.object({
  id: z.number(),
  last_name: z.string().nullish(),
  name: z.string(),
})

export const GetCustomerSetupFormTokenSchema = z
  .object({
    company: GetCustomerSetupFormTokenCompanySchema.nullish(),
    company_id: z.number().nullish(),
    created_at: z.coerce.date(),
    createdBy: GetCustomerSetupFormTokenCreatorSchema.nullish(),
    expires_at: z.coerce.date().nullish(),
    id: z.number(),
    is_usable: z.boolean(),
    setup_form_id: z.number().nullish(),
    token: CustomerSetupFormTokenValueSchema,
    updated_at: z.coerce.date(),
    used_at: z.coerce.date().nullish(),
  })
  .transform(({ createdBy, ...token }) => ({
    ...token,
    created_by: createdBy,
  }))

export const PublicCustomerSetupFormTokenContactPersonSchema = z.object({
  email: z.email(),
  last_name: z.string().nullish(),
  name: z.string(),
})

export const PublicCustomerSetupFormTokenValidationSchema = z.object({
  contact_person: PublicCustomerSetupFormTokenContactPersonSchema.nullish(),
  valid: z.boolean(),
})

const requiredVatNumber = () =>
  z
    .string()
    .trim()
    .min(1, 'Please enter company VAT number, e.g. GB123456789')
    .transform(normalizeVatNumber)
    .refine(v => VAT_NUMBER_PATTERN.test(v), VAT_NUMBER_MESSAGE)

const CONTACT_TYPES = ['transport', 'accounts'] as const

const requiredString = (label: string) => z.string().trim().min(1, `${label} is required`)

const requiredEmail = (label: string) =>
  z.string().trim().min(1, `${label} is required`).email('Please enter a valid email')

const companyRequiredEmail = () =>
  z
    .string()
    .trim()
    .min(1, 'Company email field is required')
    .email('Please enter a valid company email address, e.g. info@company.com')

const userRequiredEmail = () =>
  z
    .string()
    .trim()
    .min(1, 'Your email is required')
    .email('Please enter your email address, e.g. john.smith@company.com')

const optionalString = () =>
  z
    .string()
    .trim()
    .transform(value => value || undefined)

const optionalWebsite = () =>
  optionalString()
    .refine(
      value => value === undefined || WEBSITE_PATTERN.test(value),
      'Please enter the company website, e.g. https://www.company.com',
    )
    .transform(value => {
      if (!value) return value
      return value.match(/^https?:\/\//) ? value : `https://${value}`
    })

const nullableString = () =>
  z
    .string()
    .trim()
    .transform(value => value || null)

export const CustomerSetupFormContactTypeSchema = z.enum(CONTACT_TYPES)

const addMissingContactTypeIssues = (
  contacts: Array<{
    contact_type: z.infer<typeof CustomerSetupFormContactTypeSchema>
  }>,
  context: z.RefinementCtx,
) => {
  const contactTypes = new Set(contacts.map(contact => contact.contact_type))

  for (const contactType of CONTACT_TYPES) {
    if (contactTypes.has(contactType)) {
      continue
    }

    context.addIssue({
      code: 'custom',
      message: `A ${contactType} contact is required`,
      path: ['contacts'],
    })
  }
}

export const CustomerSetupFormVatLookupSchema = z.object({
  vat_number: requiredVatNumber(),
})

const CustomerSetupFormCompanyDetailsBaseSchema = z.object({
  address: requiredString('The company address field'),
  address_second_line: optionalString(),
  city: z.string().min(1, 'City required'),
  company_name: requiredString('The company name field'),
  country: z.string().trim().min(1, 'Please select a country'),
  email: companyRequiredEmail(),
  phone: createPhoneNumberFieldSchema({ requiredMessage: 'The company phone field is required' }),
  phone_country: PhoneCountryCodeSchema,
  postcode: z.string().min(1, 'Post code required'),
  registration_number: optionalString(),
  special_invoicing_requirements: optionalString(),
  vat_number: requiredVatNumber(),
  website: optionalWebsite(),
})

export const CustomerSetupFormCompanyDetailsSchema =
  CustomerSetupFormCompanyDetailsBaseSchema.superRefine((data, ctx) => {
    const isUkCompany = data.country === 'GB'

    if (isUkCompany && !data.registration_number?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please enter the UK company registration number, e.g. 12345678 or SC123456',
        path: ['registration_number'],
      })
    }

    addPhoneWithCountryValidation(ctx, data.phone, data.phone_country, {
      invalidMessage: 'Please enter a valid company phone number, e.g. +442079460018',
    })
  })

export const CustomerSetupFormContactSchema = z
  .object({
    contact_type: CustomerSetupFormContactTypeSchema,
    email: requiredEmail('Email'),
    last_name: requiredString('Last name'),
    name: requiredString('First name'),
    phone: createPhoneNumberFieldSchema({ requiredMessage: 'Phone is required' }),
    phone_country: PhoneCountryCodeSchema,
  })
  .superRefine((data, ctx) => {
    addPhoneWithCountryValidation(ctx, data.phone, data.phone_country, {
      invalidMessage: 'Please enter a valid phone number with at least 7 digits',
    })
  })

const CustomerSetupFormContactsBaseSchema = z.object({
  contacts: z.array(CustomerSetupFormContactSchema).min(2, 'At least two contacts are required'),
})

export const CustomerSetupFormContactsSchema = CustomerSetupFormContactsBaseSchema.superRefine(
  ({ contacts }, context) => addMissingContactTypeIssues(contacts, context),
)

export const CustomerSetupFormReferenceSchema = z
  .object({
    address: nullableString(),
    company_name: nullableString(),
    phone: createNullablePhoneNumberFieldSchema(),
    phone_country: OptionalPhoneCountryCodeSchema,
  })
  .superRefine((data, ctx) => {
    if (!data.phone) return
    addPhoneWithCountryValidation(ctx, data.phone, data.phone_country, {
      invalidMessage: 'Please enter a valid phone number',
    })
  })

export const CustomerSetupFormRequiredReferenceSchema = z
  .object({
    address: requiredString('Address'),
    company_name: requiredString('Company name'),
    phone: createPhoneNumberFieldSchema({ requiredMessage: 'Phone is required' }),
    phone_country: requiredString('Phone country').length(2, 'Please select a country'),
  })
  .superRefine((data, ctx) => {
    addPhoneWithCountryValidation(ctx, data.phone, data.phone_country, {
      invalidMessage: 'Please enter a valid phone number',
    })
  })

export const CustomerSetupFormReferencesSchema = z.object({
  references: z.array(CustomerSetupFormReferenceSchema),
})

export const CustomerSetupFormTermsSchema = z
  .object({
    terms_and_conditions_accepted: z.boolean().refine(value => value, {
      message: 'You must accept terms and conditions',
    }),
    user_email: userRequiredEmail(),
    user_last_name: requiredString('Your last name'),
    user_name: requiredString('Your first name'),
    user_phone: createPhoneNumberFieldSchema({ requiredMessage: 'Your phone is required' }),
    user_phone_country: PhoneCountryCodeSchema,
  })
  .superRefine((data, ctx) => {
    addPhoneWithCountryValidation(ctx, data.user_phone, data.user_phone_country, {
      invalidMessage: 'Please enter a valid phone number, e.g. +442079460018',
      path: 'user_phone',
    })
  })

const CustomerSetupFormInputBaseSchema = CustomerSetupFormCompanyDetailsSchema.extend(
  CustomerSetupFormContactsBaseSchema.shape,
)
  .extend(CustomerSetupFormReferencesSchema.shape)
  .extend(CustomerSetupFormTermsSchema.shape)

export const CustomerSetupFormInputSchema = CustomerSetupFormInputBaseSchema.superRefine(
  ({ contacts, user_phone, user_phone_country }, context) => {
    addMissingContactTypeIssues(contacts, context)

    addPhoneWithCountryValidation(context, user_phone, user_phone_country, {
      invalidMessage: 'Please enter a valid phone number, e.g. +442079460018',
      path: 'user_phone',
    })
  },
)

export const CustomerSetupFormSubmissionSchema = z.object({
  token: CustomerSetupFormTokenValueSchema.optional(),
  values: CustomerSetupFormInputSchema,
})

export const GetCustomerSetupFormContactSchema = z.object({
  contact_type: CustomerSetupFormContactTypeSchema,
  email: z.email(),
  id: z.number(),
  last_name: z.string().nullish(),
  name: z.string(),
  phone: z.string(),
  phone_country: z.string().length(2).nullish(),
  phone_formatted: z.string().nullish(),
})

export const GetCustomerSetupFormReferenceSchema = z.object({
  address: z.string().nullish(),
  company_name: z.string().nullish(),
  id: z.number(),
  phone: z.string().nullish(),
  phone_country: z.string().length(2).nullish(),
  phone_formatted: z.string().nullish(),
})

export const GetCustomerSetupFormBaseSchema = z.object({
  address: z.string(),
  address_second_line: z.string().nullish(),
  city: z.string().nullish(),
  company_name: z.string(),
  country: z.string().nullish(),
  created_at: z.coerce.date(),
  email: z.email(),
  id: z.number(),
  phone: z.string(),
  phone_country: z.string().length(2).nullish(),
  phone_formatted: z.string().nullish(),
  postcode: z.string().nullish(),
  registration_number: z.string().nullish(),
  special_invoicing_requirements: z.string().nullish(),
  terms_and_conditions_accepted: z.boolean(),
  updated_at: z.coerce.date(),
  user_email: z.email(),
  user_last_name: z.string().nullish(),
  user_name: z.string().nullish(),
  user_phone: z.string(),
  user_phone_country: z.string().length(2).nullish(),
  user_phone_formatted: z.string().nullish(),
  vat_number: z.string(),
  website: z.string().nullish(),
})

export const GetCustomerSetupFormSchema = GetCustomerSetupFormBaseSchema.extend({
  contacts: z.array(GetCustomerSetupFormContactSchema),
  references: z.array(GetCustomerSetupFormReferenceSchema),
  setup_form_token_id: z.number().nullish(),
  token: z
    .object({
      company: GetCustomerSetupFormTokenCompanySchema.nullish(),
      id: z.number(),
    })
    .nullish(),
})

export type CustomerSetupFormCompanyDetails = z.input<typeof CustomerSetupFormCompanyDetailsSchema>

export type CustomerSetupFormContact = z.input<typeof CustomerSetupFormContactSchema>

export type CustomerSetupFormContacts = z.input<typeof CustomerSetupFormContactsSchema>

export type CustomerSetupFormInput = z.output<typeof CustomerSetupFormInputSchema>

export type CustomerSetupFormReference = z.input<typeof CustomerSetupFormReferenceSchema>

export type CustomerSetupFormReferences = z.input<typeof CustomerSetupFormReferencesSchema>

export type CustomerSetupFormSubmission = z.output<typeof CustomerSetupFormSubmissionSchema>

export type CustomerSetupFormTerms = z.input<typeof CustomerSetupFormTermsSchema>

export type CustomerSetupFormTokenValue = z.output<typeof CustomerSetupFormTokenValueSchema>

export type GetCustomerSetupForm = z.infer<typeof GetCustomerSetupFormSchema>

export type GetCustomerSetupFormToken = z.infer<typeof GetCustomerSetupFormTokenSchema>

export type PublicCustomerSetupFormTokenValidation = z.infer<
  typeof PublicCustomerSetupFormTokenValidationSchema
>
